<template>
    <div class="contenedor-grafica-final">
        <v-btn icon small class="btn-expandir" @click="mostrarZoom = true" color="primary">
            <v-icon>mdi-fullscreen</v-icon>
        </v-btn>

        <div class="vista-previa">
            <canvas :id="`grafica-${dia}`" ref="canvasGrafica"></canvas>
        </div>

        <v-dialog v-model="mostrarZoom" max-width="98vw" transition="dialog-bottom-transition">
            <v-card color="grey lighten-4">
                <v-toolbar dark color="primary">
                    <v-btn outlined icon dark @click="mostrarZoom = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>Detalle de Distribución: {{ dia }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn text @click="descargarGraficaComoPNG">
                        <v-icon left>mdi-download</v-icon> Descargar
                    </v-btn>
                </v-toolbar>

                <v-card-text class="pa-0">
                    <div class="contenedor-zoom-limpio">
                        <canvas :id="`grafica-zoom-${dia}`" ref="canvasZoom"></canvas>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import Chart from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.plugins.register(ChartDataLabels);

export default {
    name: 'GraficaDiaFinal',
    props: {
        activo: Boolean,
        dia: { type: String, required: true },
        intervalos: Array,
        asignados: Array,
        programados: Array,
        requeridos: Array,
        maxY: { type: Number, default: null }
    },
    data() {
        return {
            chart: null,
            chartZoom: null,
            mostrarZoom: false
        }
    },
    computed: {
        // Esto soluciona lo de los picos altos (Lunes/Martes)
        maxYValue() {
            const todosLosDatos = [...(this.asignados || []), ...(this.programados || []), ...(this.requeridos || [])];
            if (todosLosDatos.length === 0) return 10;
            const max = Math.max(...todosLosDatos);
            return Math.ceil(max * 1.20); // Deja un 20% de espacio arriba para el número
        }
    },
    watch: {
        mostrarZoom(val) {
            if (val) {
                this.$nextTick(() => { this.renderizarGraficaZoom(); });
            }
        },
        maxY() {
            this.renderizarGraficaNormal();
            if (this.mostrarZoom) this.renderizarGraficaZoom();
        },
        asignados: {
            handler(n) { this.updateCharts(0, n); },
            deep: true
        }
    },
    mounted() {
        this.renderizarGraficaNormal();
    },
    methods: {
        getConfig(isZoom) {
            return {
                type: 'bar',
                data: {
                    labels: this.intervalos,
                    datasets: [
                        {
                            label: 'Programados',
                            data: this.asignados,
                            backgroundColor: 'rgba(0, 123, 255, 0.6)', // EL AZUL ORIGINAL
                            borderColor: 'rgba(0, 123, 255, 1)',
                            borderWidth: 1
                        },
                        { type: 'line', label: 'Req c/Red', data: this.programados, borderColor: 'red', fill: false, pointRadius: isZoom ? 3 : 0 },
                        { type: 'line', label: 'Req s/Red', data: this.requeridos, borderColor: 'orange', fill: false, pointRadius: isZoom ? 3 : 0 }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: { top: isZoom ? 20 : 5, bottom: 0 } // Espacio para que el número no se corte
                    },
                    plugins: {
                        datalabels: {
                            display: (ctx) => ctx.datasetIndex === 0,
                            anchor: 'end',
                            align: 'end',
                            offset: 4,
                            clip: false, // Importante para que el número no se oculte al borde
                            font: { weight: 'bold', size: isZoom ? 11 : 9 },
                            formatter: (value) => {
                                // Esto muestra el valor tal cual viene en el array de datos
                                return value;
                            },
                            rotation: -45,
                            // --- ESTO EVITA QUE LA LÍNEA LO "TACHE" ---
                            color: '#000',
                            font: { weight: 'bold', size: 10 },
                            textStrokeColor: '#fff', // Borde blanco
                            textStrokeWidth: 4,      // Grosor del borde
                            // -----------------------------------------
                        }
                    },
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                max: this.maxY || this.maxYValue // Aplicación del máximo dinámico
                            }
                        }],
                        xAxes: [{
                            ticks: {
                                autoSkip: true,
                                maxRotation: 45,
                                minRotation: 45
                            }
                        }]
                    }
                }
            };
        },
        renderizarGraficaNormal() {
            const ctx = this.$refs.canvasGrafica.getContext('2d');
            if (this.chart) this.chart.destroy();
            this.chart = new Chart(ctx, this.getConfig(false));
        },
        renderizarGraficaZoom() {
            const ctx = this.$refs.canvasZoom.getContext('2d');
            if (this.chartZoom) this.chartZoom.destroy();
            this.chartZoom = new Chart(ctx, this.getConfig(true));
        },
        updateCharts(index, data) {
            if (this.chart) { this.chart.data.datasets[index].data = data; this.chart.update(); }
            if (this.chartZoom) { this.chartZoom.data.datasets[index].data = data; this.chartZoom.update(); }
        },
        descargarGraficaComoPNG() {
            const link = document.createElement('a');
            link.href = this.chartZoom.toBase64Image();
            link.download = `grafica-${this.dia}.png`;
            link.click();
        }
    }
}
</script>

<style scoped>
.contenedor-grafica-final {
    position: relative;
    overflow: hidden;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 10px;
    background: white;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.btn-expandir {
    position: absolute;
    right: 5px;
    top: 5px;
    z-index: 2;
    /* Aumentamos para que siempre esté por encima */
    /* Sugerencia: Un fondo leve para que no se pierda con la leyenda */
    background-color: rgba(255, 255, 255, 0.7) !important;
}

.vista-previa {
    height: 350px;
    width: 100%;
    position: relative;
}

.contenedor-zoom-limpio {
    width: 100%;
    height: 75vh;
    /* Ajuste para eliminar el espacio muerto abajo */
    padding: 20px;
    background: white;
}
</style>